import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Fade,
  Grow,
  useMediaQuery,
  useTheme,
  keyframes,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import HomeIcon from '@mui/icons-material/Home';
import YardIcon from '@mui/icons-material/Yard';
import BusinessIcon from '@mui/icons-material/Business';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ChairIcon from '@mui/icons-material/Chair';
import KitchenIcon from '@mui/icons-material/Kitchen';
import BedIcon from '@mui/icons-material/Bed';
import TvIcon from '@mui/icons-material/Tv';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import RoomIllustration from '../components/booking/RoomIllustration';
import MobileProgress from '../components/booking/MobileProgress';
import MobileBottomBar, { MobileBottomBarSpacer } from '../components/booking/MobileBottomBar';
import MobileTimeSlots from '../components/booking/MobileTimeSlots';
import { mobileInputProps, scrollBookingTop, touchIconBtn, SAFE_TOP } from '../components/booking/bookingUi';
import PublicBrandHeader from '../components/public/PublicBrandHeader';
import PublicBrandShell from '../components/public/PublicBrandShell';
import { usePublicBrandContext } from '../context/PublicBrandContext';
import { brandHeaderStyle } from '../utils/publicBrand';
import BookingInstallBanner from '../components/booking/BookingInstallBanner';
import TurnstileWidget from '../components/booking/TurnstileWidget';
import { enableBookingPwa, disableBookingPwa, isStandaloneDisplay } from '../utils/bookingPwa';
import { API_BASE_URL } from '../api';

const BOOKING_FORM_ID = 'public-booking-form';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

function isValidUkPostcode(value) {
  const t = String(value || '').trim().toUpperCase();
  if (t.length < 5) return false;
  return /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/.test(t);
}

function formatPostcode(value) {
  const raw = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (raw.length <= 3) return raw;
  const out = `${raw.slice(0, -3)} ${raw.slice(-3)}`;
  return out.trim();
}

const M3_PER_YD3 = 0.764555;

const CATEGORY_META = {
  home: { label: 'Home', Icon: HomeIcon, color: '#1565c0', soft: '#e3f2fd' },
  garden: { label: 'Garden', Icon: YardIcon, color: '#2e7d32', soft: '#e8f5e9' },
  office: { label: 'Office', Icon: BusinessIcon, color: '#6a1b9a', soft: '#f3e5f5' },
};

function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function basketPayloadFromCounts(counts) {
  return Object.entries(counts)
    .map(([id, q]) => ({ waste_catalog_item_id: Number(id), quantity: q }))
    .filter((r) => r.quantity > 0);
}

function formatTierPrice(s) {
  return s.unit_price != null ? `£${Number(s.unit_price).toFixed(0)}` : '—';
}

function itemIconForName(name) {
  const n = String(name || '').toLowerCase();
  if (n.includes('sofa') || n.includes('chair') || n.includes('desk')) return ChairIcon;
  if (n.includes('bed') || n.includes('mattress') || n.includes('wardrobe')) return BedIcon;
  if (n.includes('fridge') || n.includes('cooker') || n.includes('microwave') || n.includes('washing')) return KitchenIcon;
  if (n.includes('tv') || n.includes('monitor') || n.includes('computer') || n.includes('printer')) return TvIcon;
  return LocalShippingOutlinedIcon;
}

function buildCatalogIndex(catalog) {
  const idx = {};
  ['home', 'garden', 'office'].forEach((cat) => {
    (catalog[cat] || []).forEach((row) => {
      idx[row.id] = { ...row, category: cat };
    });
  });
  return idx;
}

function PublicBookingPage({ embedMode: embedModeProp = false }) {
  const { brand: brandFromCtx } = usePublicBrandContext();
  const location = useLocation();
  const embedMode =
    embedModeProp ||
    location.pathname.endsWith('/embed') ||
    new URLSearchParams(location.search).get('embed') === '1';

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isMobile = !isDesktop;

  const [flowMode, setFlowMode] = useState('calculator');
  const [step, setStep] = useState(0);

  const steps = useMemo(() => {
    if (flowMode === 'tier_only') return ['Postcode', 'Load tier', 'Date & time', 'Your details'];
    return ['Add items', 'Postcode', 'Get prices', 'Date & time', 'Your details'];
  }, [flowMode]);

  const [catalog, setCatalog] = useState({ home: [], garden: [], office: [] });
  const [limits, setLimits] = useState({ max_volume_m3: 40, max_basket_lines: 80 });
  const [services, setServices] = useState([]);
  const [slotsRes, setSlotsRes] = useState(null);

  const [catTab, setCatTab] = useState('home');
  const [counts, setCounts] = useState({});

  const basketLines = useMemo(() => basketPayloadFromCounts(counts), [counts]);
  const catalogIndex = useMemo(() => buildCatalogIndex(catalog), [catalog]);

  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [date, setDate] = useState(todayYmd);
  const [time, setTime] = useState('');
  const [serviceId, setServiceId] = useState('');

  const [clientName, setClientName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [captchaConfig, setCaptchaConfig] = useState({ enabled: false, site_key: null });
  const [turnstileToken, setTurnstileToken] = useState('');
  const [postcode, setPostcode] = useState('');
  const [basketDrawerOpen, setBasketDrawerOpen] = useState(false);

  const postcodeOk = useMemo(() => isValidUkPostcode(postcode), [postcode]);
  const activeStepLabel = steps[Math.min(step, steps.length - 1)] || '';
  const postcodeDisplay = postcodeOk ? formatPostcode(postcode) : '';

  const recommendedId = quote?.suggested_price_book_item_id != null ? String(quote.suggested_price_book_item_id) : '';
  const effectiveTierId = serviceId || recommendedId || '';

  const loadTierById = useMemo(() => {
    if (!effectiveTierId) return null;
    return services.find((s) => String(s.id) === effectiveTierId) || null;
  }, [effectiveTierId, services]);

  const loadTiers = useMemo(
    () =>
      [...services].sort((a, b) => {
        const ay = Number(a.typical_load_yd3 ?? (a.typical_load_m3 || 0) / M3_PER_YD3);
        const by = Number(b.typical_load_yd3 ?? (b.typical_load_m3 || 0) / M3_PER_YD3);
        return ay - by;
      }),
    [services]
  );

  const totalItems = useMemo(() => Object.values(counts).reduce((a, b) => a + b, 0), [counts]);
  const totalVolumeYd3 = quote?.total_volume_yd3 != null ? Number(quote.total_volume_yd3) : 0;
  const totalWeightKg = quote?.total_weight_kg_est != null ? Number(quote.total_weight_kg_est) : 0;

  const basketRows = useMemo(() => {
    return Object.entries(counts)
      .filter(([, q]) => q > 0)
      .map(([id, qty]) => {
        const row = catalogIndex[id];
        return row ? { ...row, qty } : null;
      })
      .filter(Boolean);
  }, [counts, catalogIndex]);

  const loadInitial = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const [svcRes, catRes, cfgRes] = await Promise.all([
        fetch(`${API_BASE_URL}public/booking/services`, { headers: { Accept: 'application/json' } }),
        fetch(`${API_BASE_URL}public/booking/catalog`, { headers: { Accept: 'application/json' } }),
        fetch(`${API_BASE_URL}public/booking/config`, { headers: { Accept: 'application/json' } }),
      ]);
      const svcJson = await svcRes.json().catch(() => ({}));
      const catJson = await catRes.json().catch(() => ({}));
      const cfgJson = await cfgRes.json().catch(() => ({}));
      if (!svcRes.ok) throw new Error(svcJson?.message || `HTTP ${svcRes.status}`);
      if (!catRes.ok) throw new Error(catJson?.message || `HTTP ${catRes.status}`);
      if (cfgRes.ok && cfgJson?.captcha) {
        setCaptchaConfig({
          enabled: !!cfgJson.captcha.enabled,
          site_key: cfgJson.captcha.site_key || null,
        });
      }
      setServices(Array.isArray(svcJson.data) ? svcJson.data : []);
      if (catJson.data) {
        setCatalog({
          home: catJson.data.home || [],
          garden: catJson.data.garden || [],
          office: catJson.data.office || [],
        });
      }
      if (catJson.limits) setLimits(catJson.limits);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSlots = useCallback(async () => {
    if (!date) return;
    setSlotsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}public/booking/slots?${new URLSearchParams({ date })}`, {
        headers: { Accept: 'application/json' },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
      setSlotsRes(json);
      const first = json?.slots?.[0]?.time;
      setTime((t) => (t && json.slots?.some((s) => s.time === t) ? t : first || ''));
    } catch (e) {
      setSlotsRes(null);
    } finally {
      setSlotsLoading(false);
    }
  }, [date]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  useEffect(() => {
    setStep(0);
    setCounts({});
    setServiceId('');
    setQuote(null);
    setPostcode('');
    setError('');
    setSuccess('');
  }, [flowMode]);

  useEffect(() => {
    if (flowMode !== 'calculator' || basketLines.length === 0) {
      if (flowMode !== 'calculator') setQuote(null);
      return;
    }
    const h = setTimeout(async () => {
      setQuoteLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}public/booking/quote`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ basket: basketLines }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
        setQuote(json);
      } catch (e) {
        setQuote(null);
        if (step === 2) setError(e.message || 'Quote failed');
      } finally {
        setQuoteLoading(false);
      }
    }, 350);
    return () => clearTimeout(h);
  }, [flowMode, basketLines, step]);

  function setQty(itemId, delta) {
    setCounts((prev) => {
      const cur = prev[itemId] || 0;
      const next = Math.max(0, Math.min(99, cur + delta));
      const out = { ...prev };
      if (next === 0) delete out[itemId];
      else out[itemId] = next;
      if (Object.keys(out).length > (limits.max_basket_lines || 80)) return prev;
      return out;
    });
  }

  function clearBasket() {
    setCounts({});
    setQuote(null);
    setServiceId('');
  }

  const captchaRequired = Boolean(captchaConfig.enabled && captchaConfig.site_key);
  const canSubmit = Boolean(
    date &&
      time &&
      clientName.trim() &&
      email.trim() &&
      effectiveTierId &&
      !submitting &&
      (!captchaRequired || turnstileToken)
  );

  async function onSubmit(e) {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);
    try {
      const body = {
        client_name: clientName.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        address: address.trim() || undefined,
        scheduled_date: date,
        scheduled_time: time,
        notes: notes.trim() || undefined,
        price_book_item_id: Number(effectiveTierId),
      };
      if (flowMode === 'calculator' && basketLines.length > 0) body.basket = basketLines;
      if (postcodeOk) body.postcode = postcodeDisplay;
      if (captchaRequired && turnstileToken) body.turnstile_token = turnstileToken;

      const res = await fetch(`${API_BASE_URL}public/booking`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 429) {
        throw new Error('Too many booking attempts. Please wait a minute and try again.');
      }
      if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
      setTurnstileToken('');
      const successMsg =
        json?.message ||
        'Thank you — your request is in our system as a pending job (Online booking). We will confirm your collection shortly.';
      setSuccess(successMsg);
      if (embedMode && typeof window !== 'undefined' && window.parent !== window) {
        try {
          window.parent.postMessage(
            {
              type: 'workwise:booking:success',
              job_id: json?.job_id ?? null,
              message: successMsg,
            },
            '*'
          );
        } catch {
          /* ignore */
        }
      }
      clearBasket();
      setStep(0);
    } catch (err) {
      setError(err.message || 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  }

  function goToPostcode() {
    if (flowMode === 'calculator' && basketLines.length === 0) return;
    setBasketDrawerOpen(false);
    setStep(flowMode === 'calculator' ? 1 : 0);
    scrollBookingTop();
  }

  function goToPrices() {
    if (!postcodeOk) return;
    setStep(flowMode === 'calculator' ? 2 : 1);
    scrollBookingTop();
  }

  function goToSchedule() {
    if (!effectiveTierId) return;
    if (!serviceId && recommendedId) setServiceId(recommendedId);
    setStep(flowMode === 'calculator' ? 3 : 2);
    scrollBookingTop();
  }

  function goToDetails() {
    if (!date || !time) return;
    setStep(flowMode === 'calculator' ? 4 : 3);
    scrollBookingTop();
  }

  useEffect(() => {
    document.title = 'Book a collection';
    if (!embedMode) {
      void enableBookingPwa();
    }
    return () => {
      document.title = 'WorkWiseSoft';
      if (!embedMode) {
        disableBookingPwa();
      }
    };
  }, [embedMode]);

  useEffect(() => {
    scrollBookingTop();
  }, [step, flowMode]);

  /* ——— Sticky basket panel ——— */
  function BasketPanel({ showGetPrices }) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <ShoppingCartOutlinedIcon color="primary" />
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Your basket
          </Typography>
          {totalItems > 0 ? <Chip size="small" label={totalItems} color="primary" /> : null}
        </Stack>

        {basketRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Add items from {CATEGORY_META[catTab]?.label || 'a room'} to build your estimate.
          </Typography>
        ) : (
          <Box sx={{ maxHeight: 280, overflow: 'auto', mb: 1.5 }}>
            {basketRows.map((row) => {
              const ItemIco = itemIconForName(row.name);
              return (
                <Stack
                  key={row.id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                    <ItemIco sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                        {row.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ×{row.qty}
                      </Typography>
                    </Box>
                  </Stack>
                  <IconButton size="small" onClick={() => setQty(row.id, -row.qty)} aria-label="Remove">
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              );
            })}
          </Box>
        )}

        <Stack spacing={1} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Volume
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {quoteLoading ? '…' : `${totalVolumeYd3.toFixed(2)} yd³`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Est. weight
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {quoteLoading ? '…' : `~${totalWeightKg} kg`}
            </Typography>
          </Box>
        </Stack>

        {showGetPrices ? (
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={basketRows.length === 0}
            onClick={goToPostcode}
            sx={{ py: 1.25, fontWeight: 800 }}
          >
            Continue
          </Button>
        ) : null}

        {basketRows.length > 0 ? (
          <Button fullWidth size="small" color="inherit" sx={{ mt: 1 }} onClick={clearBasket}>
            Clear basket
          </Button>
        ) : null}
      </Paper>
    );
  }

  /* ——— Step: Add items ——— */
  function renderAddItems() {
    const currentItems = catalog[catTab] || [];
    const meta = CATEGORY_META[catTab];

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          alignItems: { md: 'flex-start' },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>
            What do you need collecting?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isMobile
              ? 'Pick a room, then tap + on each item. Your basket stays at the bottom.'
              : 'Pick a room, then tap + on each item. Your basket updates on the right.'}
          </Typography>

          {/* Room selector — horizontal scroll on mobile */}
          <Stack
            direction="row"
            spacing={1}
            sx={{
              mb: 2,
              flexWrap: { xs: 'nowrap', sm: 'nowrap' },
              overflowX: { xs: 'auto', sm: 'visible' },
              pb: { xs: 0.5, sm: 0 },
              mx: { xs: -0.5, sm: 0 },
              px: { xs: 0.5, sm: 0 },
              WebkitOverflowScrolling: 'touch',
              '& > *': { flex: { xs: '0 0 auto', sm: 1 }, minWidth: { xs: 108, sm: 0 } },
            }}
          >
            {['home', 'garden', 'office'].map((key) => {
              const m = CATEGORY_META[key];
              const Ico = m.Icon;
              const active = catTab === key;
              const countInRoom = (catalog[key] || []).reduce((sum, row) => sum + (counts[row.id] || 0), 0);
              return (
                <Card
                  key={key}
                  variant="outlined"
                  onClick={() => setCatTab(key)}
                  sx={{
                    flex: { xs: '1 1 30%', sm: 1 },
                    minWidth: 0,
                    cursor: 'pointer',
                    textAlign: 'center',
                    py: 1.5,
                    px: 1,
                    borderWidth: active ? 2 : 1,
                    borderColor: active ? m.color : 'divider',
                    bgcolor: active ? m.soft : 'background.paper',
                    transition: 'all 0.25s ease',
                    transform: active ? 'scale(1.02)' : 'none',
                    '&:hover': { borderColor: m.color, boxShadow: 3 },
                  }}
                >
                  <Ico sx={{ fontSize: 32, color: m.color }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.8rem' }}>
                    {m.label}
                  </Typography>
                  {countInRoom > 0 ? (
                    <Chip size="small" label={countInRoom} color="primary" sx={{ mt: 0.5, height: 22 }} />
                  ) : null}
                </Card>
              );
            })}
          </Stack>

          <Fade in key={catTab} timeout={400}>
            <Box sx={{ mb: 3, animation: `${fadeUp} 0.4s ease` }}>
              <RoomIllustration category={catTab} accent={meta.color} soft={meta.soft} />
              <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mt: 1 }}>
                Explore your {meta.label.toLowerCase()} — tap + on items below
              </Typography>
            </Box>
          </Fade>

          {/* Items grid */}
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: meta.color }}>
            In your {meta.label.toLowerCase()}
          </Typography>

          {currentItems.length === 0 ? (
            <Alert severity="info">No items in this category yet.</Alert>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 1.5,
              }}
            >
              {currentItems.map((row, idx) => {
                const qty = counts[row.id] || 0;
                const ItemIco = itemIconForName(row.name);
                return (
                  <Grow in key={`${catTab}-${row.id}`} timeout={300 + idx * 40}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderColor: qty > 0 ? meta.color : 'divider',
                        borderWidth: qty > 0 ? 2 : 1,
                        bgcolor: qty > 0 ? meta.soft : 'background.paper',
                      }}
                    >
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: 1.5,
                              bgcolor: 'background.paper',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <ItemIco sx={{ color: meta.color }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                              {row.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ≈ {Number(row.volume_m3).toFixed(2)} m³ · ~{row.weight_kg_est} kg
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.25 }}>
                              <IconButton
                                onClick={() => setQty(row.id, -1)}
                                disabled={qty === 0}
                                aria-label={`Remove one ${row.name}`}
                                sx={{ ...touchIconBtn, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}
                              >
                                <RemoveIcon />
                              </IconButton>
                              <Chip label={qty} sx={{ minWidth: 48, height: 36, fontWeight: 800, fontSize: '1rem' }} color={qty ? 'primary' : 'default'} />
                              <IconButton
                                onClick={() => setQty(row.id, 1)}
                                aria-label={`Add one ${row.name}`}
                                sx={{ ...touchIconBtn, bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } }}
                              >
                                <AddIcon />
                              </IconButton>
                            </Stack>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grow>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Sticky basket — desktop */}
        {isDesktop ? (
          <Box
            sx={{
              width: { md: 280, lg: 300 },
              flexShrink: 0,
              position: 'sticky',
              top: 16,
              alignSelf: 'flex-start',
              maxHeight: 'calc(100vh - 32px)',
            }}
          >
            <BasketPanel showGetPrices />
          </Box>
        ) : null}
      </Box>
    );
  }

  /* ——— Step: Collection postcode (logistics only — same London prices everywhere) ——— */
  function renderPostcode() {
    const backStep = flowMode === 'calculator' ? 0 : null;
    const title = 'Collection postcode';
    const subtitle =
      flowMode === 'calculator'
        ? 'Enter the London postcode where we should collect. Prices are the same across London — this helps us plan your visit.'
        : 'Enter the London postcode for your collection, then choose a load size.';

    return (
      <Fade in timeout={400}>
        <Box sx={{ maxWidth: 520, mx: 'auto', animation: `${fadeUp} 0.35s ease` }}>
          {backStep != null ? (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => setStep(backStep)}
              sx={{ mb: 2, minHeight: isMobile ? 44 : undefined }}
            >
              Back to items
            </Button>
          ) : null}

          <Card variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box
              sx={{
                py: 3,
                px: 2,
                textAlign: 'center',
                background: 'linear-gradient(180deg, #e3f2fd 0%, #fff 100%)',
              }}
            >
              <LocationOnOutlinedIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 900 }}>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, px: 2 }}>
                {subtitle}
              </Typography>
            </Box>
            <CardContent>
              <TextField
                label="London postcode"
                placeholder="e.g. SW1A 1AA"
                fullWidth
                value={postcode}
                onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                onBlur={() => {
                  if (postcode.trim()) setPostcode(formatPostcode(postcode));
                }}
                error={postcode.length > 0 && !postcodeOk}
                helperText={
                  postcode.length > 0 && !postcodeOk
                    ? 'Enter a valid UK postcode'
                    : 'We serve all London postcodes. Prices do not vary by area.'
                }
                inputProps={{
                  ...mobileInputProps,
                  autoComplete: 'postal-code',
                  inputMode: 'text',
                  style: { ...mobileInputProps.style, letterSpacing: '0.06em', fontWeight: 700 },
                }}
                sx={{ mb: isMobile ? 1 : 3 }}
              />
              {!isMobile ? (
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={!postcodeOk}
                  onClick={goToPrices}
                  sx={{ py: 1.25, fontWeight: 800, minHeight: 48 }}
                >
                  {flowMode === 'calculator' ? 'Continue to prices' : 'Continue'}
                </Button>
              ) : null}
            </CardContent>
          </Card>
        </Box>
      </Fade>
    );
  }

  /* ——— Step: Get prices (dedicated screen) ——— */
  function renderGetPrices() {
    const recommended = recommendedId ? loadTiers.find((s) => String(s.id) === recommendedId) : null;

    return (
      <Fade in timeout={400}>
        <Box sx={{ animation: `${fadeUp} 0.35s ease` }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => setStep(1)}
          sx={{ mb: 2, minHeight: isMobile ? 44 : undefined }}
        >
          Back to postcode
        </Button>

        <Paper
          style={brandHeaderStyle(brandFromCtx)}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            color: 'white',
          }}
        >
          {postcodeDisplay ? (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1, opacity: 0.95 }}>
              <LocationOnOutlinedIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {postcodeDisplay}
              </Typography>
            </Stack>
          ) : null}
          <Typography variant="overline" sx={{ opacity: 0.9 }}>
            Your estimate
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
            {quoteLoading ? '…' : `${totalVolumeYd3.toFixed(2)} cubic yards`}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.95 }}>
            Estimated weight ~{totalWeightKg} kg · {totalItems} item{totalItems !== 1 ? 's' : ''} in basket
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.85 }}>
            Standard London rates · indicative only. Final load and price confirmed on site.
          </Typography>
        </Paper>

        {recommended ? (
          <Card
            sx={{
              mb: 3,
              border: '2px solid',
              borderColor: 'secondary.main',
              bgcolor: (t) => (t.palette.mode === 'light' ? 'rgba(156, 39, 176, 0.08)' : 'rgba(156, 39, 176, 0.15)'),
            }}
          >
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <CheckCircleOutlineIcon color="secondary" />
                <Typography variant="overline" color="secondary.main" sx={{ fontWeight: 800 }}>
                  Recommended for you
                </Typography>
              </Stack>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>
                {recommended.name}
              </Typography>
              <Typography variant="h4" color="primary" sx={{ fontWeight: 900, my: 1 }}>
                from {formatTierPrice(recommended)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {recommended.typical_load_yd3} yd³ · max {recommended.max_weight_kg} kg · {recommended.duration_minutes} min labour
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                fullWidth={isMobile}
                sx={{ mt: 2, fontWeight: 800, minHeight: 48 }}
                onClick={() => setServiceId(String(recommended.id))}
              >
                {serviceId === String(recommended.id) ? 'Selected' : 'Select this load'}
              </Button>
            </CardContent>
          </Card>
        ) : quoteLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : null}

        <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
          All available loads
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 1.5,
          }}
        >
          {loadTiers.map((s) => {
            const id = String(s.id);
            const selected = serviceId === id;
            const isRec = recommendedId === id;
            const yd = s.typical_load_yd3 ?? (s.typical_load_m3 ? Number(s.typical_load_m3) / M3_PER_YD3 : '—');

            return (
                <Card
                  key={s.id}
                  variant={selected ? 'elevation' : 'outlined'}
                  onClick={() => setServiceId(id)}
                  sx={{
                    cursor: 'pointer',
                    borderWidth: selected ? 2 : 1,
                    borderColor: selected ? 'primary.main' : isRec ? 'secondary.light' : 'divider',
                    position: 'relative',
                    minHeight: { xs: 88, sm: 'auto' },
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  {isRec ? (
                    <Chip
                      label="Recommended"
                      color="secondary"
                      size="small"
                      sx={{ position: 'absolute', top: 8, right: 8 }}
                    />
                  ) : null}
                  <CardContent sx={{ py: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      {yd} yd³ load
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.25, pr: isRec ? 5 : 0 }}>
                      {s.name.replace(' Cubic Yard Load', '')}
                    </Typography>
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 900, my: 0.5 }}>
                      from {formatTierPrice(s)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {s.duration_minutes} min · max {s.max_weight_kg} kg
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                      Guide price — no payment online
                    </Typography>
                  </CardContent>
                </Card>
            );
          })}
        </Box>

        {!isMobile ? (
          <Box sx={{ mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              disabled={!effectiveTierId}
              onClick={goToSchedule}
              sx={{ fontWeight: 800, px: 4, minHeight: 48 }}
            >
              Continue — pick date & time
            </Button>
          </Box>
        ) : null}
        </Box>
      </Fade>
    );
  }

  function renderLoadTierOnly() {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => setStep(0)} sx={{ mb: 2 }}>
          Back to postcode
        </Button>
        {postcodeDisplay ? (
          <Chip
            icon={<LocationOnOutlinedIcon />}
            label={postcodeDisplay}
            variant="outlined"
            sx={{ mb: 2 }}
          />
        ) : null}
        <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
          Choose your load size
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.5 }}>
          {loadTiers.map((s) => {
            const id = String(s.id);
            const selected = serviceId === id;
            return (
              <Card
                key={s.id}
                variant={selected ? 'elevation' : 'outlined'}
                onClick={() => setServiceId(id)}
                sx={{
                  cursor: 'pointer',
                  borderWidth: selected ? 2 : 1,
                  borderColor: selected ? 'primary.main' : 'divider',
                  minHeight: 88,
                }}
              >
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    {s.name}
                  </Typography>
                  <Typography variant="h5" color="primary" sx={{ fontWeight: 900 }}>
                    from {formatTierPrice(s)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {s.typical_load_yd3} yd³ · {s.duration_minutes} min · max {s.max_weight_kg} kg
                  </Typography>
                </CardContent>
              </Card>
            );
          })}
        </Box>
        {!isMobile ? (
          <Button variant="contained" sx={{ mt: 3, minHeight: 48 }} disabled={!serviceId} onClick={() => setStep(2)}>
            Continue
          </Button>
        ) : null}
      </Box>
    );
  }

  function renderSchedule() {
    const back = flowMode === 'calculator' ? 2 : 1;
    return (
      <Card variant="outlined">
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>
            When should we come?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose a date and time slot for your collection.
          </Typography>
          <TextField
            label="Preferred date"
            type="date"
            fullWidth
            size={isMobile ? 'medium' : 'small'}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={mobileInputProps}
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
            Time slot
          </Typography>
          {isMobile ? (
            <MobileTimeSlots
              slots={slotsRes?.slots}
              value={time}
              onChange={setTime}
              loading={slotsLoading}
            />
          ) : (
            <FormControl fullWidth size="small" disabled={slotsLoading}>
              <InputLabel>Time slot</InputLabel>
              <Select label="Time slot" value={time} onChange={(e) => setTime(e.target.value)}>
                {(slotsRes?.slots || []).map((s) => (
                  <MenuItem key={s.time} value={s.time}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {!isMobile ? (
            <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
              <Button variant="contained" disabled={!date || !time} onClick={goToDetails} sx={{ minHeight: 48 }}>
                Continue
              </Button>
              <Button variant="text" onClick={() => setStep(back)}>
                Back
              </Button>
            </Stack>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  function renderDetails() {
    const back = flowMode === 'calculator' ? 3 : 2;
    const fieldSize = isMobile ? 'medium' : 'small';
    return (
      <Card variant="outlined">
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>
            Your details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No online payment — we will confirm your booking and final price before collection.
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
            {postcodeDisplay ? <Chip icon={<LocationOnOutlinedIcon />} label={postcodeDisplay} variant="outlined" /> : null}
            {loadTierById ? <Chip label={loadTierById.name} color="primary" /> : null}
            {flowMode === 'calculator' ? <Chip label={`~${totalVolumeYd3.toFixed(2)} yd³`} variant="outlined" /> : null}
            <Chip label={date && time ? `${date} @ ${time}` : 'Pick slot'} variant="outlined" />
          </Stack>
          <TextField
            label="Your name"
            required
            fullWidth
            size={fieldSize}
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            autoComplete="name"
            inputProps={mobileInputProps}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Email"
            type="email"
            required
            fullWidth
            size={fieldSize}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            inputProps={{ ...mobileInputProps, inputMode: 'email' }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Phone"
            fullWidth
            size={fieldSize}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="tel"
            inputProps={{ ...mobileInputProps, inputMode: 'tel' }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Collection address"
            fullWidth
            size={fieldSize}
            multiline
            minRows={2}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            autoComplete="street-address"
            inputProps={mobileInputProps}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Notes (optional)"
            fullWidth
            size={fieldSize}
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            inputProps={mobileInputProps}
            sx={{ mb: 2 }}
          />
          {captchaRequired ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Security check
              </Typography>
              <TurnstileWidget
                siteKey={captchaConfig.site_key}
                onToken={setTurnstileToken}
                onError={() => setTurnstileToken('')}
              />
            </Box>
          ) : null}
          {!isMobile ? (
            <Stack direction="row" spacing={1}>
              <Button type="submit" variant="contained" size="large" disabled={!canSubmit} sx={{ fontWeight: 800, minHeight: 48 }}>
                {submitting ? <CircularProgress size={22} color="inherit" /> : 'Request booking'}
              </Button>
              <Button variant="text" onClick={() => setStep(back)}>
                Back
              </Button>
            </Stack>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  const isLastStep =
    (flowMode === 'calculator' && step === 4) || (flowMode === 'tier_only' && step === 3);

  const mobileBottomBar = useMemo(() => {
    if (!isMobile || loading || success) return null;

    if (flowMode === 'tier_only') {
      if (step === 0) {
        return {
          primaryLabel: 'Choose load size',
          onPrimary: goToPrices,
          primaryDisabled: !postcodeOk,
          summaryTitle: 'London collection',
          summarySubtitle: 'Enter your postcode to continue',
        };
      }
      if (step === 1) {
        return {
          primaryLabel: 'Pick date & time',
          onPrimary: () => {
            setStep(2);
            scrollBookingTop();
          },
          primaryDisabled: !serviceId,
          summaryTitle: loadTierById?.name || 'Select a load',
          summarySubtitle: loadTierById ? `from ${formatTierPrice(loadTierById)}` : 'Tap a load size above',
        };
      }
      if (step === 2) {
        return {
          primaryLabel: 'Your details',
          onPrimary: goToDetails,
          primaryDisabled: !date || !time,
          summaryTitle: date && time ? `${date} · ${time}` : 'Pick a date and time',
        };
      }
      if (step === 3) {
        return {
          primaryLabel: submitting ? 'Sending…' : 'Request booking',
          onPrimary: () => document.getElementById(BOOKING_FORM_ID)?.requestSubmit(),
          primaryDisabled: !canSubmit,
          primaryLoading: submitting,
          summaryTitle: 'Almost done',
          summarySubtitle: 'No payment online — we will confirm by email or phone',
        };
      }
      return null;
    }

    if (step === 0) {
      return {
        primaryLabel: 'Continue',
        onPrimary: goToPostcode,
        primaryDisabled: totalItems === 0,
        secondaryLabel: totalItems > 0 ? `Basket (${totalItems})` : undefined,
        onSecondary: totalItems > 0 ? () => setBasketDrawerOpen(true) : undefined,
        summaryTitle: totalItems > 0 ? `${totalItems} items · ${totalVolumeYd3.toFixed(2)} yd³` : 'Your basket is empty',
        summarySubtitle: totalItems > 0 ? `~${totalWeightKg} kg estimated` : 'Add items to continue',
      };
    }
    if (step === 1) {
      return {
        primaryLabel: 'Continue to prices',
        onPrimary: goToPrices,
        primaryDisabled: !postcodeOk,
        summaryTitle: postcodeDisplay || 'Collection postcode',
      };
    }
    if (step === 2) {
      return {
        primaryLabel: 'Pick date & time',
        onPrimary: goToSchedule,
        primaryDisabled: !effectiveTierId,
        summaryTitle: loadTierById?.name || 'Choose your load',
        summarySubtitle: loadTierById ? `${formatTierPrice(loadTierById)} · guide only` : undefined,
      };
    }
    if (step === 3) {
      return {
        primaryLabel: 'Your details',
        onPrimary: goToDetails,
        primaryDisabled: !date || !time,
        summaryTitle: date && time ? `${date} · ${time}` : 'Select date & time',
      };
    }
    if (step === 4) {
      return {
        primaryLabel: submitting ? 'Sending…' : 'Request booking',
        onPrimary: () => document.getElementById(BOOKING_FORM_ID)?.requestSubmit(),
        primaryDisabled: !canSubmit,
        primaryLoading: submitting,
        summaryTitle: 'Confirm your request',
        summarySubtitle: 'No payment now — pay after we confirm on site',
      };
    }
    return null;
  }, [
    isMobile,
    loading,
    success,
    flowMode,
    step,
    postcodeOk,
    postcodeDisplay,
    serviceId,
    effectiveTierId,
    loadTierById,
    date,
    time,
    totalItems,
    totalVolumeYd3,
    totalWeightKg,
    canSubmit,
    submitting,
  ]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100dvh',
          pt: SAFE_TOP,
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading booking…
        </Typography>
      </Box>
    );
  }

  const mainContent = (() => {
    if (flowMode === 'tier_only') {
      if (step === 0) return renderPostcode();
      if (step === 1) return renderLoadTierOnly();
      if (step === 2) return renderSchedule();
      return renderDetails();
    }
    if (step === 0) return renderAddItems();
    if (step === 1) return renderPostcode();
    if (step === 2) return renderGetPrices();
    if (step === 3) return renderSchedule();
    return renderDetails();
  })();

  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          pt: `calc(16px + ${SAFE_TOP})`,
          px: 2,
          pb: 4,
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Card sx={{ maxWidth: 420, width: '100%', textAlign: 'center', borderRadius: 3, p: 1 }}>
            <CardContent sx={{ py: 4 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 900, mb: 1 }}>
                Request received
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                {success}
              </Typography>
              <Alert severity="info" sx={{ textAlign: 'left', mb: 3 }}>
                No payment was taken online. We will contact you to confirm your collection and final price.
              </Alert>
              <Button
                fullWidth
                variant="contained"
                size="large"
                sx={{ minHeight: 48, fontWeight: 800 }}
                onClick={() => {
                  setSuccess('');
                  setStep(0);
                  scrollBookingTop();
                }}
              >
                Book another collection
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
      }}
    >
      {!embedMode ? (
        <Box sx={isMobile ? { pt: SAFE_TOP } : undefined}>
          <PublicBrandHeader
            subtitle="Book a collection · no online payment"
            compact={isMobile}
            sticky={isMobile}
          />
        </Box>
      ) : null}

      <Box
        sx={{
          maxWidth: 1100,
          mx: 'auto',
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 4 },
          pb: mobileBottomBar ? { xs: 0, md: 4 } : undefined,
        }}
      >
        {!embedMode ? (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, display: { xs: 'none', md: 'block' } }}>
            Add your items, enter your London postcode, choose a load — then pick a time.
          </Typography>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        ) : null}

        {!embedMode ? <BookingInstallBanner /> : null}

        {!embedMode && isStandaloneDisplay() && isMobile ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textAlign: 'center' }}>
            Installed app
          </Typography>
        ) : null}

        <Card variant="outlined" sx={{ mb: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
          <CardContent sx={{ py: { xs: 1.25, md: 1.5 }, '&:last-child': { pb: { xs: 1.25, md: 1.5 } } }}>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size={isMobile ? 'medium' : 'small'}
              orientation={isMobile ? 'vertical' : 'horizontal'}
              value={flowMode}
              onChange={(_, v) => v && setFlowMode(v)}
              sx={{
                '& .MuiToggleButton-root': {
                  py: isMobile ? 1.25 : 0.75,
                  fontWeight: 700,
                  textTransform: 'none',
                  whiteSpace: 'normal',
                  lineHeight: 1.3,
                },
              }}
            >
              <ToggleButton value="calculator">I need help estimating</ToggleButton>
              <ToggleButton value="tier_only">I know my load size</ToggleButton>
            </ToggleButtonGroup>
          </CardContent>
        </Card>

        {isMobile ? (
          <MobileProgress step={step} total={steps.length} label={activeStepLabel} />
        ) : (
          <Stepper activeStep={Math.min(step, steps.length - 1)} alternativeLabel sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        <form id={BOOKING_FORM_ID} onSubmit={onSubmit}>
          {mainContent}
          {mobileBottomBar ? <MobileBottomBarSpacer tall={isLastStep} /> : null}
        </form>
      </Box>

      {mobileBottomBar ? <MobileBottomBar {...mobileBottomBar} /> : null}

      <Drawer
        anchor="bottom"
        open={basketDrawerOpen}
        onClose={() => setBasketDrawerOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            maxHeight: '85dvh',
            pb: mobileBottomBar ? 10 : 2,
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
          <Box sx={{ width: 40, height: 4, bgcolor: 'divider', borderRadius: 2, mx: 'auto', mb: 2 }} />
          <BasketPanel showGetPrices />
        </Box>
      </Drawer>
    </Box>
  );
}

export default function PublicBooking(props) {
  const [embeddedBrand, setEmbeddedBrand] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}public/brand`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((j) => {
        if (!cancelled && j?.data) setEmbeddedBrand(j.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <PublicBrandShell embeddedBrand={embeddedBrand}>
      <PublicBookingPage {...props} />
    </PublicBrandShell>
  );
}
